import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { SmbOnboardingProvider, useSmbOnboarding } from "../../contexts/SmbOnboardingContext";
import { createBusiness, getOnboardingStatus } from "../../api/smb.api";
import BusinessInfoStep from "../../components/smb/onboarding/BusinessInfoStep";
import SocialMediaStep from "../../components/smb/onboarding/SocialMediaStep";
import WebsiteStep from "../../components/smb/onboarding/WebsiteStep";
import CallingAgentStep from "../../components/smb/onboarding/CallingAgentStep";
import PaymentStep from "../../components/smb/onboarding/PaymentStep";

const steps = ["Business Info", "Social Media", "Website", "Calling Agent", "Payment"];

const SmbOnboardingContent = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");
  const {
    business,
    currentStep,
    goToStep,
    completeStep,
    loadBusiness,
    isLoading,
    setBusiness,
  } = useSmbOnboarding();

  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (businessId) {
        await loadBusiness(businessId);
      } else {
        // Check if user already has a business
        const status = await getOnboardingStatus();
        if (status?.status && status?.data?.has_business) {
          navigate(`/smb/onboarding/${status.data.business_id}`, { replace: true });
          return;
        }
      }
      setInitializing(false);
    };
    init();
  }, [businessId, loadBusiness, navigate]);

  const handleBusinessCreated = async (newBusiness) => {
    setBusiness(newBusiness);
    navigate(`/smb/onboarding/${newBusiness.id}`, { replace: true });
    await goToStep(1);
  };

  if (initializing || isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <CircularProgress sx={{ color: "#3B82F6" }} />
      </Box>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <BusinessInfoStep onComplete={handleBusinessCreated} existingBusiness={business} />;
      case 1:
        return <SocialMediaStep onComplete={completeStep} businessId={business?.id} />;
      case 2:
        return <WebsiteStep onComplete={completeStep} businessId={business?.id} />;
      case 3:
        return <CallingAgentStep onComplete={completeStep} businessId={business?.id} />;
      case 4:
        return <PaymentStep businessId={business?.id} />;
      default:
        return <BusinessInfoStep onComplete={handleBusinessCreated} />;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#101220", color: "#fff", px: isMobile ? 2 : 4, py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: "center" }}>
        Set Up Your Business
      </Typography>

      <Stepper
        activeStep={currentStep}
        alternativeLabel={!isMobile}
        orientation={isMobile ? "vertical" : "horizontal"}
        sx={{
          mb: 4,
          "& .MuiStepLabel-label": { color: "#9CA3AF", fontSize: isMobile ? "0.75rem" : "0.875rem" },
          "& .MuiStepLabel-label.Mui-active": { color: "#3B82F6" },
          "& .MuiStepLabel-label.Mui-completed": { color: "#10B981" },
          "& .MuiStepIcon-root": { color: "#282D51" },
          "& .MuiStepIcon-root.Mui-active": { color: "#3B82F6" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#10B981" },
          "& .MuiStepConnector-line": { borderColor: "#282D51" },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ maxWidth: 800, mx: "auto" }}>{renderStep()}</Box>
    </Box>
  );
};

const SmbOnboardingWizard = () => {
  const { setShowLoginModal } = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem("isLoggedIn");

  useEffect(() => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      navigate("/vic", { replace: true });
    }
  }, [isLoggedIn, setShowLoginModal, navigate]);

  if (!isLoggedIn) return null;

  return (
    <SmbOnboardingProvider>
      <SmbOnboardingContent />
    </SmbOnboardingProvider>
  );
};

export default SmbOnboardingWizard;
